import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { dispatch } from 'store';
import { getPaymentIdAndLockToken } from 'services/RenewalService';
import { setPaymentData, setPolicyLockToken } from 'actions/expiryRenewalActions';
import { Store } from 'types/store';
import { Amendment, ProductAmendment } from 'types/expiryRenewal';

type ErrorModal = {
  message: string;
  title: string;
};

type UseFetchPaymentDataProps = {
  setErrorModal: (_error: ErrorModal) => void;
  usePreviousCardDetails?: boolean;
};

type UseFetchPaymentDataResult = {
  loading: boolean;
  amendments: Array<ProductAmendment | Amendment>;
};

const useFetchPaymentData = ({
  setErrorModal,
  usePreviousCardDetails = false,
}: UseFetchPaymentDataProps): UseFetchPaymentDataResult => {
  const { uid, sessionId, customerId, memberNo, isTescoLogin } = useSelector((state: Store) => state.session);
  const upgrades = useSelector((state: Store) => state.expiryRenewal.addOns.selected);
  // Both vehicle and member amendments posted as the create payload's Amendments.
  const stagedAmendments = useSelector((state: Store) => state.expiryRenewal.amendments);
  // Held breakdown extras the member dropped on the customise step - product codes only.
  const removedExtras = useSelector((state: Store) => state.expiryRenewal.removedExtras);
  const { pageModel, toggles, monthlyFlex } = useSelector((state: Store) => state.expiryRenewal);

  const isMountedRef = useRef(true);

  const [loading, setLoading] = useState(true);

  let amendments: Array<ProductAmendment | Amendment> = [];

  const fetchRenewalPaymentData = async () => {
    if (!pageModel || !toggles) return;

    if (upgrades?.length > 0) {
      amendments = upgrades.map((upgrade) => ({
        productCode: upgrade?.productCode,
        amendmentType: 'Add' as const,
      }));
    }

    if (stagedAmendments?.length > 0) {
      amendments = [...amendments, ...stagedAmendments];
    }

    if (removedExtras?.length > 0) {
      const removalAmendments = removedExtras.map((productCode) => ({
        productCode,
        amendmentType: 'Remove' as const,
      }));
      amendments = [...amendments, ...removalAmendments];
    }

    const payload = {
      CustomerId: customerId,
      MembershipNumber: memberNo,
      RenewalRiskVersion: pageModel.renewalRiskVersion,
      StateToken: pageModel.stateToken,
      EnablePaperless: toggles.paperless,
      EnableAutoRenew: toggles.autoRenew,
      MemberChoseSwitchToMonthlyFlex: monthlyFlex,
      OneMonthFreeDiscount: pageModel.isEligibleForOneMonthFreeDiscount,
      Amendments: amendments,
      UsePreviousCardDetails: usePreviousCardDetails,
      IsTescoReferral: isTescoLogin,
      LockInformation: {
        UserId: uid,
        SessionId: sessionId,
      },
    };

    try {
      const response = await getPaymentIdAndLockToken(payload);

      if (!isMountedRef.current) {
        return;
      }

      if (response?.payload) {
        const { lockToken, displayErrorMessage, errorMessage, errorTitle, errorReloadRisk } = response.payload;

        if (displayErrorMessage) {
          setErrorModal({ message: errorMessage, title: errorReloadRisk ? `Reload error: ${errorTitle}` : errorTitle });
          setLoading(false);
          return;
        }

        dispatch(setPaymentData(response.payload));
        dispatch(setPolicyLockToken(lockToken));
      }
    } catch {
      if (isMountedRef.current) {
        setErrorModal({
          message: 'Error fetching payment data',
          title: 'Request failed',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRenewalPaymentData();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { loading, amendments };
};

export default useFetchPaymentData;
